// src/components/admin/ProductStats.jsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

const ProductStats = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalProducts: 0,
    totalRevenue: 0,
    weightSummary: [],
    activeProducts: 0,
    expiredProducts: 0,
    productsByCategory: [],
    averagePrice: 0
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [message, setMessage] = useState('');

  const fetchProductStats = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setMessage('');

      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

      console.log('🔄 Fetching product stats...');

      const response = await fetch(`${API_URL}/api/admin/stats/product-stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(user?.token && { 'Authorization': `Bearer ${user.token}` })
        }
      });

      const result = await response.json();

      if (result.success) {
        setStats(result.data);
        if (result.message) setMessage(result.message);
        console.log('✅ Product stats loaded:', result.data);
      } else {
        throw new Error(result.message || 'Failed to fetch product stats');
      }
    } catch (err) {
      console.error('❌ Error fetching product stats:', err);
      // Use mock data as fallback
      setStats({
        totalProducts: 543,
        totalRevenue: 125430,
        weightSummary: [
          { unit: 'kg', total: 2450, count: 156 },
          { unit: 'liter', total: 1200, count: 89 },
          { unit: 'bags', total: 450, count: 78 },
          { unit: 'packets', total: 890, count: 120 }
        ],
        activeProducts: 487,
        expiredProducts: 56,
        productsByCategory: [
          { category: 'Fertiliser', count: 123 },
          { category: 'Seedlings', count: 89 },
          { category: 'Vegetables', count: 156 },
          { category: 'Fruits', count: 98 },
          { category: 'Grains', count: 77 }
        ],
        averagePrice: 231
      });
      setMessage('Using demo data - ' + err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchProductStats();
  }, []);

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-KE', {
      style: 'currency',
      currency: 'KES'
    }).format(amount);
  };

  // Calculate percentages for progress bars
  const activePercentage = (stats.activeProducts / stats.totalProducts) * 100;
  const expiredPercentage = (stats.expiredProducts / stats.totalProducts) * 100;

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-teal-100 p-6 animate-fade-in">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="h-7 w-48 bg-gray-200 rounded-lg mb-2 animate-pulse"></div>
            <div className="h-4 w-32 bg-gray-100 rounded animate-pulse"></div>
          </div>
          <div className="h-9 w-24 bg-gray-200 rounded-lg animate-pulse"></div>
        </div>

        <div className="space-y-6">
          {/* Summary Cards Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2].map((item) => (
              <div key={item} className="p-5 bg-gradient-to-r from-teal-50 to-teal-25 rounded-xl border border-teal-100 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="space-y-2 flex-1">
                    <div className="h-4 w-32 bg-teal-200 rounded"></div>
                    <div className="h-8 w-24 bg-teal-300 rounded"></div>
                    <div className="h-3 w-40 bg-teal-100 rounded"></div>
                  </div>
                  <div className="w-14 h-14 bg-teal-200 rounded-2xl"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Status Progress Skeleton */}
          <div className="p-4 bg-gray-50 rounded-xl animate-pulse">
            <div className="h-4 w-40 bg-gray-200 rounded mb-3"></div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 rounded w-full"></div>
              <div className="h-3 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>

          {/* Categories Skeleton */}
          <div className="animate-pulse">
            <div className="h-5 w-40 bg-gray-200 rounded mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((item) => (
                <div key={item} className="flex items-center justify-between">
                  <div className="h-4 w-32 bg-gray-200 rounded"></div>
                  <div className="h-4 w-12 bg-gray-300 rounded"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-teal-100 p-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Product Statistics</h3>
          <p className="text-sm text-gray-500 mt-1">Inventory overview and revenue analytics</p>
        </div>

        <div className="flex items-center gap-3">
          {message && (
            <span className="px-3 py-1.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1.5">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              Demo Data
            </span>
          )}

          <button
            onClick={() => fetchProductStats(true)}
            disabled={refreshing}
            className="flex items-center gap-2 px-2 py-1 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-sm"
          >
            <svg
              className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {/* Total Products Card */}
        <div className="group p-5 bg-gradient-to-r from-teal-50 to-teal-25 rounded-xl border border-teal-200 hover:border-teal-300 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm font-semibold text-teal-700 uppercase tracking-wide">Total Products</p>
                <div className="w-2 h-2 bg-teal-500 rounded-full animate-pulse"></div>
              </div>
              <p className="text-xl font-bold text-teal-900 mb-3">{stats.totalProducts.toLocaleString()}</p>

              {/* Status Progress Bars */}
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-green-700 font-medium">Active</span>
                  <span className="text-green-700">{stats.activeProducts} ({activePercentage.toFixed(1)}%)</span>
                </div>
                <div className="w-full bg-green-100 rounded-full h-2">
                  <div
                    className="bg-green-500 h-2 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${activePercentage}%` }}
                  ></div>
                </div>

                <div className="flex items-center justify-between text-xs">
                  <span className="text-red-700 font-medium">Expired</span>
                  <span className="text-red-700">{stats.expiredProducts} ({expiredPercentage.toFixed(1)}%)</span>
                </div>
                <div className="w-full bg-red-100 rounded-full h-2">
                  <div
                    className="bg-red-500 h-2 rounded-full transition-all duration-1000 ease-out"
                    style={{ width: `${expiredPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
            <div className="w-10 h-10 bg-teal-500 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
              </svg>
            </div>
          </div>
        </div>

        {/* Total Revenue Card */}
        <div className="group p-5 bg-gradient-to-r from-purple-50 to-teal-25 rounded-xl border border-purple-200 hover:border-purple-300 shadow-sm hover:shadow-md transition-all duration-300">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <p className="text-sm font-semibold text-purple-700 uppercase tracking-wide">Total Revenue</p>
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              </div>
              <p className="text-xl font-bold text-purple-900 mb-2">{formatCurrency(stats.totalRevenue)}</p>
              <div className="flex items-center gap-2 text-xs text-purple-600 bg-purple-100 px-3 py-1.5 rounded-lg">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
                <span>Average: {formatCurrency(stats.averagePrice)} per product</span>
              </div>
            </div>
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-teal-400 rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform duration-300">
              <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Products by Category */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <svg className="w-4 h-4 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
            Products by Category
          </h4>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            Top {Math.min(stats.productsByCategory.length, 5)}
          </span>
        </div>

        <div className="space-y-3">
          {stats.productsByCategory.slice(0, 5).map((item, index) => {
            const percentage = (item.count / stats.totalProducts) * 100;
            return (
              <div key={index} className="group flex items-center justify-between p-3 bg-white border border-gray-100 rounded-xl hover:border-teal-200 hover:shadow-sm transition-all duration-200">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center shadow-sm">
                    <span className="text-xs font-bold text-gray-500">{index + 1}</span>
                  </div>
                  <span className="text-xs font-medium text-gray-700 capitalize">{item.category}</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-20 bg-gray-100 rounded-full h-2">
                    <div
                      className="bg-gradient-to-r from-teal-500 to-teal-400 h-2 rounded-full transition-all duration-1000 ease-out"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                  <span className="text-xs font-semibold text-teal-900 min-w-10 text-right">
                    {item.count}
                  </span>
                </div>
              </div>
            );
          })}

          {stats.productsByCategory.length === 0 && (
            <div className="text-center p-6 bg-gray-50 rounded-xl border border-gray-200">
              <svg className="w-12 h-12 text-gray-400 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-sm text-gray-500">No category data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-gray-100">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {message ? 'Demo data for testing purposes' : 'Live inventory and revenue data'}
        </div>

        <div className="flex items-center gap-1 text-xs text-gray-400">
          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Auto-refreshes every 5 minutes
        </div>
      </div>
    </div>
  );
};

export default ProductStats;